import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Card, Row, Col, Button, Spinner } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { BASE_URL, paybridge_Url } from "../../Config";
import "../../Pages/Company/Company.css";

const CurrentDayRecord = () => {
  const [data, setData] = useState({
    userId: "",
    name: "",
  });

  const [summaryData, setSummaryData] = useState({
    total_payments: 0,
    completed_count: 0,
    pending_count: 0,
    failed_count: 0,
  });

  const [profileId, setProfileId] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = async (companyProfileId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}Company_Dashboard?CompanyId=${companyProfileId}`
      );
      if (response.data) {
        setData({
          userId: response.data.userId,
          name: response.data.companyName || "Company",
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch dashboard data.", "error");
    }
  };

  const fetchSummaryData = async (companyProfileId) => {
    try {
      if (!companyProfileId) return;

      const payload = {
        companyId: companyProfileId,
        type: "currentDay",
      };

      const response = await axios.post(
        `${paybridge_Url}report/company/summary`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        setSummaryData(response.data.data);
      } else {
        Swal.fire("Error", "No summary data returned from API", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch summary data.", "error");
    }
  };

  useEffect(() => {
    const storedUserData = sessionStorage.getItem("user");
    if (!storedUserData) {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
      return;
    }

    const userData = JSON.parse(storedUserData);
    const companyProfileId =
      userData.userRole === "Manager" || userData.userRole === "Admin"
        ? userData.selectedCompanyId || userData.companyId || userData.companyProfileId
        : userData.userId || userData.companyProfileId || userData.companyId;

    setProfileId(companyProfileId);

    if (companyProfileId) {
      fetchDashboardData(companyProfileId);
      fetchSummaryData(companyProfileId);
    } else {
      Swal.fire(
        "Error",
        "No company is selected. Please open a company from the Manager Dashboard.",
        "error"
      );
    }
  }, []);

  const handleRefresh = async () => {
    if (!profileId) return;
    setRefreshing(true);
    await fetchSummaryData(profileId);
    setRefreshing(false);
  };

  return (
    <Card className="co-card">
      <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
        <div>
          <h5 className="mb-0">
            <i className="bi bi-calendar-day me-2 text-primary" />
            {data.name || "Company"} — Current Day
          </h5>
          <small className="text-muted">Today&apos;s payment summary</small>
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? (
            <Spinner animation="border" size="sm" className="me-1" />
          ) : (
            <i className="bi bi-arrow-clockwise me-1" />
          )}
          Refresh
        </Button>
      </Card.Header>

      <Card.Body>
        <Row className="g-3">
          <InfoCard
            title="Total Payments"
            value={summaryData.total_payments}
            icon="bi-cash-stack"
            variant="total"
          />
          <InfoCard
            title="Completed"
            value={summaryData.completed_count}
            icon="bi-check-circle"
            variant="ok"
          />
          <InfoCard
            title="Pending"
            value={summaryData.pending_count}
            icon="bi-hourglass-split"
            variant="pending"
          />
          <InfoCard
            title="Failed"
            value={summaryData.failed_count}
            icon="bi-x-circle"
            variant="fail"
          />
        </Row>
      </Card.Body>
    </Card>
  );
};

const InfoCard = ({ title, value, icon, variant }) => (
  <Col xs={12} sm={6} lg={3}>
    <div className={`co-stat co-stat-${variant} p-3`}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="text-muted small fw-semibold">{title}</span>
        <span className="co-stat-icon">
          <i className={`bi ${icon}`} />
        </span>
      </div>
      <div className="fs-3 fw-bold text-dark">{value ?? 0}</div>
    </div>
  </Col>
);

export default CurrentDayRecord;
